/**
 * AgreementWorkflow — the day-to-day "generate agreement → review → send"
 * flow, mounted on the Loan Detail page. Zero JSON, zero template-admin
 * concerns. Staff never leaves this component to complete the funding path.
 *
 * Stages:
 *   1. Load: fetch /agreements/preview-context/{loan_id}
 *   2. Ready-to-generate: show live-data summary + auto-picked template
 *      (with dropdown override) + [Generate] button.
 *   3. Draft: PDF inline preview + [Review/Edit] + [Regenerate] + [Send].
 *   4. Sent: locked; show envelope status.
 *
 * Regeneration semantics (per 2026-09 product decision):
 *   - Draft:  overwrite in place (same agreement id, timeline preserved).
 *   - Sent+:  blocked — must void envelope or create a new version.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  FileSignature, Send, RotateCcw, Edit3, Loader2, ChevronDown,
  CheckCircle2, AlertCircle, ArrowRight,
} from "lucide-react";
import AgreementReviewForm from "./AgreementReviewForm";

const STAGES = [
  { key: "approved", label: "Approved" },
  { key: "generated", label: "Generated" },
  { key: "reviewed", label: "Reviewed" },
  { key: "sent", label: "Sent to signers" },
  { key: "signed", label: "Fully signed" },
  { key: "ready_to_fund", label: "Ready to fund" },
  { key: "disbursed", label: "Disbursed" },
];

function ProgressStrip({ currentStageKey }) {
  const idx = STAGES.findIndex(s => s.key === currentStageKey);
  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid="workflow-progress-strip">
      {STAGES.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <React.Fragment key={s.key}>
            <div
              data-testid={`workflow-stage-${s.key}`}
              className={
                "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-xs font-medium " +
                (done
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : active
                    ? "bg-[#0066FF] text-white"
                    : "bg-white text-[#9CA3AF] border border-[#E5E7EB]")
              }
            >
              {done && <CheckCircle2 className="w-3 h-3" />}
              {s.label}
            </div>
            {i < STAGES.length - 1 && (
              <ArrowRight className="w-3 h-3 text-[#D1D5DB]" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function AgreementWorkflow({ loanId, loan }) {
  const [ctx, setCtx] = useState(null);        // /preview-context response
  const [loading, setLoading] = useState(true);
  const [selectedTplId, setSelectedTplId] = useState(null);
  const [agreement, setAgreement] = useState(null);   // active draft agreement
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(null);   // "generating" | "regenerating" | "review" | "sending"
  const [showReview, setShowReview] = useState(false);

  const currentStage = (() => {
    if (!agreement) return "approved";
    const s = agreement.status;
    if (s === "draft") return "generated";
    if (s === "sent") return "sent";
    if (s === "signed" || s === "executed") return "signed";
    if (s === "finalized") return "reviewed";
    return "approved";
  })();

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/agreements/preview-context/${loanId}`);
      setCtx(data);
      setSelectedTplId(data.auto_selected_template.template_id);
      // Reuse the newest draft if one exists — no reason to make the user
      // regenerate manually.
      const latestDraft = (data.existing_agreements || []).find(a => a.status === "draft");
      if (latestDraft) {
        const full = await api.get(`/agreements/${latestDraft.id}`);
        setAgreement(full.data);
        await fetchPdf(latestDraft.id);
      } else {
        setAgreement(null);
        setPdfUrl(null);
      }
    } catch (e) {
      toast.error("Could not load agreement context: " + formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  const fetchPdf = async (agreementId) => {
    try {
      const r = await api.get(`/agreements/${agreementId}/preview.pdf`,
                               { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      toast.error("PDF preview failed: " + formatApiError(e));
    }
  };

  useEffect(() => { loadContext(); }, [loadContext]);

  const generate = async () => {
    setBusy("generating");
    try {
      const { data } = await api.post("/agreements/preview", {
        loan_id: loanId,
        template_id: selectedTplId,
        overrides: {},
      });
      setAgreement(data.agreement);
      await fetchPdf(data.agreement._id);
      toast.success("Agreement generated");
    } catch (e) {
      toast.error("Generation failed: " + formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    if (!agreement) return;
    setBusy("regenerating");
    try {
      const { data } = await api.post(`/agreements/${agreement._id}/regenerate`);
      const full = await api.get(`/agreements/${agreement._id}`);
      setAgreement(full.data);
      await fetchPdf(agreement._id);
      if (data.template_upgrade) {
        toast.success(
          `Regenerated on newer template v${data.template_upgrade.to_version} ` +
          `(was v${data.template_upgrade.from_version}).`
        );
      } else {
        toast.success("Regenerated from live data");
      }
    } catch (e) {
      toast.error("Regenerate failed: " + formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const saveReview = async (form) => {
    if (!agreement) return;
    setBusy("review");
    try {
      await api.post(`/agreements/${agreement._id}/review-edit`, form);
      const full = await api.get(`/agreements/${agreement._id}`);
      setAgreement(full.data);
      await fetchPdf(agreement._id);
      setShowReview(false);
      toast.success("Saved and re-rendered");
    } catch (e) {
      toast.error("Save failed: " + formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const sendToSigners = async () => {
    if (!agreement) return;
    if (!window.confirm("Send this agreement to signers via DocuSign? Once sent it cannot be edited.")) return;
    setBusy("sending");
    try {
      // Finalize first (freezes the snapshot), then send.
      await api.post(`/agreements/${agreement._id}/finalize`, { confirmed: true });
      await api.post(`/agreements/${agreement._id}/send`, { provider: "docusign" });
      const full = await api.get(`/agreements/${agreement._id}`);
      setAgreement(full.data);
      toast.success("Sent for signature");
    } catch (e) {
      toast.error("Send failed: " + formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-[#6B7280]"
           data-testid="agreement-workflow-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading agreement workflow…
      </div>
    );
  }
  if (!ctx) return null;

  const isDraft = agreement?.status === "draft";
  const isLocked = agreement && !isDraft;
  const pc = ctx.preview_context;

  return (
    <div data-testid="agreement-workflow" className="space-y-5">
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-4">
        <ProgressStrip currentStageKey={currentStage} />
      </div>

      {/* Live-data summary — always visible, single source of truth for what
          will populate the agreement */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-5"
           data-testid="agreement-workflow-livedata">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-[#0A2540]">Will populate from</div>
          {!agreement && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#6B7280]">Template:</label>
              <div className="relative">
                <select
                  value={selectedTplId || ""}
                  onChange={e => setSelectedTplId(e.target.value)}
                  data-testid="workflow-template-select"
                  className="appearance-none h-8 pr-7 pl-3 text-xs border border-[#E5E7EB] rounded-md bg-white"
                >
                  {ctx.available_templates.map(t => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.code} v{t.version}
                      {t.template_id === ctx.auto_selected_template.template_id ? " (auto)" : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <SummaryRow label="Client" value={pc.client_name} />
          <SummaryRow label="Attorney" value={pc.attorney_name} />
          <SummaryRow label="Client email" value={pc.client_email} />
          <SummaryRow label="Firm" value={pc.attorney_firm} />
          <SummaryRow label="Client phone" value={pc.client_phone} />
          <SummaryRow label="Case type" value={pc.case_type_description} />
          <SummaryRow label="Loan amount" value={pc.loan_amount ? `$${Number(pc.loan_amount).toLocaleString()}` : ""} />
          <SummaryRow label="Accident date" value={pc.accident_date} />
          <SummaryRow label="Funding date" value={(pc.funding_date || "").slice(0, 10)} />
        </div>
      </div>

      {/* Primary action */}
      {!agreement && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-5">
          <Button
            onClick={generate}
            disabled={busy === "generating"}
            data-testid="workflow-generate"
            className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC] text-white"
          >
            {busy === "generating" ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><FileSignature className="w-4 h-4 mr-2" /> Generate Agreement</>
            )}
          </Button>
        </div>
      )}

      {/* Draft — preview + actions */}
      {agreement && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 space-y-4"
             data-testid="workflow-draft-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-[#0A2540]">
                {isDraft ? "Draft agreement" : `Agreement — ${agreement.status}`}
              </div>
              <div className="text-xs text-[#6B7280]">
                {agreement.template_code} v{agreement.template_version} · created {(agreement.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isDraft && (
                <>
                  <Button
                    onClick={() => setShowReview(v => !v)}
                    disabled={busy != null}
                    variant="outline"
                    data-testid="workflow-review-toggle"
                    className="border-[#E5E7EB]"
                  >
                    <Edit3 className="w-4 h-4 mr-2" /> {showReview ? "Hide review" : "Review / edit"}
                  </Button>
                  <Button
                    onClick={regenerate}
                    disabled={busy != null}
                    variant="outline"
                    data-testid="workflow-regenerate"
                    className="border-[#E5E7EB]"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {busy === "regenerating" ? "Regenerating…" : "Regenerate"}
                  </Button>
                  <Button
                    onClick={sendToSigners}
                    disabled={busy != null}
                    data-testid="workflow-send"
                    className="bg-[#0A2540] hover:bg-[#1a3556] text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {busy === "sending" ? "Sending…" : "Send to DocuSign"}
                  </Button>
                </>
              )}
              {isLocked && (
                <div className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full text-xs border border-amber-200 bg-amber-50 text-amber-800">
                  <AlertCircle className="w-3.5 h-3.5" /> Locked — envelope active
                </div>
              )}
            </div>
          </div>

          {isDraft
            && ctx.auto_selected_template
            && agreement.template_code === ctx.auto_selected_template.code
            && agreement.template_version !== ctx.auto_selected_template.version && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                   data-testid="workflow-template-outdated-hint">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  This draft is on <b>v{agreement.template_version}</b> but the current approved
                  version is <b>v{ctx.auto_selected_template.version}</b>. Click <b>Regenerate</b>
                  to auto-upgrade — the draft ID and timeline stay the same.
                </span>
              </div>
            )}

          {showReview && isDraft && (
            <div className="border-t border-[#E5E7EB] pt-4">
              <AgreementReviewForm
                previewContext={pc}
                initialForm={agreement.review_form || {}}
                onSave={saveReview}
                onReset={regenerate}
                saving={busy === "review" || busy === "regenerating"}
              />
            </div>
          )}

          {pdfUrl ? (
            <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
              <div className="flex items-center justify-between px-3 h-9 bg-[#F9FAFB] border-b border-[#E5E7EB] text-xs text-[#6B7280]">
                <span>Preview</span>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="workflow-pdf-open-new-tab"
                  className="text-[#0066FF] hover:underline"
                >
                  Open in new tab ↗
                </a>
              </div>
              <iframe
                src={pdfUrl}
                title="Agreement preview"
                className="w-full block"
                style={{ height: "800px" }}
                data-testid="workflow-pdf-iframe"
              />
            </div>
          ) : (
            <div className="border border-dashed border-[#E5E7EB] rounded-md h-64 flex items-center justify-center text-sm text-[#9CA3AF]">
              PDF preview not available — click Regenerate to rebuild.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[#9CA3AF] uppercase text-[10px] tracking-wider min-w-[100px]">{label}</span>
      <span className="text-[#0A2540] font-medium truncate">
        {value || <span className="text-[#D1D5DB] italic font-normal">not set</span>}
      </span>
    </div>
  );
}
